import React, { useState, useEffect } from 'react';
import Joyride, { Step, CallBackProps, STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useGuide } from '../hooks/useGuide';
import { useI18n } from '../hooks/useI18n';

/**
 * 陪练引导组件 - 跨页面全局引导
 * 
 * 使用方式：在需要展示引导的页面中引入此组件
 * <TrainingGuider />
 * 
 * 引导步骤包括：
 * 1. TabBar "我的" 标签 - 查看指派给自己的陪练任务
 * 2. ToAdmin 按钮 - 管理员可以去后台创建陪练
 * 
 * 自动适配：如果目标元素不存在（如权限不足），会自动跳过该步骤
 */
const TrainingGuider: React.FC = () => {
  const { t } = useI18n();

  // 定义所有可能的引导步骤
  const allPossibleSteps: Step[] = [
    {
      target: '[data-tour="my-tab"]',
      content: (
        <div>
          {t('guide.myTaskPrefix', '可在此查看')}
          <span style={{ 
            borderRadius: '4px',
            fontWeight: 600,
            color: '#fef08a'
          }}>
            {t('guide.assignedToSelf', '指派给自己')}
          </span>
          {t('guide.myTaskSuffix', '的陪练任务')}
        </div>
      ),
      title: '',
      disableBeacon: true,
      placement: 'top',
      isFixed: true,  // ✅ TabBar 是 fixed 定位
      offset: 10,  // 提示框与目标元素的距离
      spotlightPadding: 5,  // 高亮区域的内边距
    //   hideFooter: true,  // 隐藏底部所有按钮
      hideCloseButton: true,   // 隐藏右上角关闭按钮（X）

      styles: {
        options: {
        //   arrowColor: '#fff',
        },
      },
    },
    {
      target: '[data-tour="to-admin-btn"]',
      content: (
        <div>
          {t('guide.adminCanGo', '管理员可以去')}
          <span style={{ 
            borderRadius: '4px',
            fontWeight: 600,
            color: '#fef08a'
          }}>
            {t('guide.backend', '后台')}
          </span>
          {t('guide.createTrainingTask', '创建陪练任务')}
        </div>
      ),
      title: t('guide.adminBackendTitle', '⚙️ 管理后台'),
      placement: 'left',
      isFixed: true,  // ✅ ToAdminFixedBtn 是 fixed 定位
      offset: 10,  // 提示框与目标元素的距离
      spotlightPadding: 5,  // 高亮区域的内边距
      styles: {
        options: {
          arrowColor: '#fff',
        },
      },
    },
  ];

  // 动态过滤出存在的步骤
  const [guideSteps, setGuideSteps] = useState<Step[]>([]);

  // 延迟检测元素是否存在，确保 DOM 完全加载
  useEffect(() => {
    // 延迟检测，给 DOM 足够的渲染时间
    const timer = setTimeout(() => {
      const filteredSteps = allPossibleSteps.filter(step => {
        // 检查目标元素是否存在
        if (typeof step.target === 'string') {
          const element = document.querySelector(step.target);
          if (element) {
            const rect = element.getBoundingClientRect();
            console.log(`🔍 [${t('guide.guideDetection', '引导检测')}] ${t('guide.element', '元素')} ${step.target}:`, {
              [t('guide.exists', '存在')]: '✅',
              [t('guide.position', '位置')]: `top: ${rect.top}, left: ${rect.left}, bottom: ${rect.bottom}`,
              [t('guide.size', '尺寸')]: `width: ${rect.width}, height: ${rect.height}`
            });
          } else {
            console.log(`🔍 [${t('guide.guideDetection', '引导检测')}] ${t('guide.element', '元素')} ${step.target}: ❌ ${t('guide.notExist', '不存在')}`);
          }
          return element !== null;
        }
        return true;
      });
      
      console.log(`📋 [${t('guide.guideDetection', '引导检测')}] ${t('guide.finalStepCount', '最终引导步骤数量')}: ${filteredSteps.length}`);
      setGuideSteps(filteredSteps);
    }, 1200); // 增加延迟到 1200ms，确保所有动画和渲染完成

    return () => clearTimeout(timer);
  }, []);

  const { run, steps, handleJoyrideCallback } = useGuide('training-guide', guideSteps);

  // 自定义引导回调，处理蒙层点击逻辑
  const [stepIndex, setStepIndex] = useState(0);
  
  const customCallback = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    
    console.log('🎯 [引导回调]', { action, index, status, type, totalSteps: steps.length });
    
    // 更新当前步骤索引
    if (type === 'step:after') {
      setStepIndex(index);
    }
    
    // 处理点击蒙层（overlay）的情况
    if (action === 'close' ) {
      // 判断是否是最后一步
      const isLastStep = index === steps.length - 1;
      
      if (isLastStep) {
        // 最后一步：关闭引导
        console.log('✅ [引导] 最后一步，关闭引导');
        handleJoyrideCallback(data);
      } else {
        // 非最后一步：阻止关闭，进入下一步
        console.log('➡️ [引导] 点击蒙层，进入下一步');
        // 不调用 handleJoyrideCallback，让引导继续
        // 注意：这里不做任何操作，因为我们设置了 continuous={true}
        // 引导会自动继续到下一步
      }
      return;
    }
    
    // 其他情况正常处理
    handleJoyrideCallback(data);
  };

  // 锁定页面滚动，防止引导时内容错位
  useEffect(() => {
    if (run) {
      // 保存当前滚动位置
      const scrollY = window.scrollY;
      
      // 锁定滚动
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      console.log('🔒 [引导] 页面滚动已锁定');
      
      return () => {
        // 恢复滚动
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        
        // 恢复滚动位置
        window.scrollTo(0, scrollY);
        
        console.log('🔓 [引导] 页面滚动已恢复');
      };
    }
  }, [run]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      callback={handleJoyrideCallback}
      spotlightClicks={false}
      disableOverlayClose={true}
      disableScrolling={true}  // 禁用自动滚动
      // disableScrollParentFix={true}  // 移除此项，保留默认的滚动父元素修复
      styles={{
        options: {
          primaryColor: '#9333ea', // 紫色主题
          textColor: '#fff',
          backgroundColor: 'transparent',
          arrowColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 10000,
          width: 300,
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
          fontSize: 15,
          lineHeight: 1.6,
        //   backgroundColor: 'transparent',
          border:'1px dashed #fff'
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontSize: 16,
          fontWeight: 600,
          marginBottom: 12,
        },
        tooltipContent: {
          padding: ' 0',
        },
       
        spotlight: {
          borderRadius: 40,
          border: '2px solid #fff', // 虚线边框
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6), 0 0 20px rgba(147, 51, 234, 0.5)',
        },
        beacon: {
          display: 'none',
        },
      }}
      locale={{
        back: t('guide.back', '上一步'),
        close: t('guide.close', '关闭'),
        last: t('guide.last', '完成'),
        next: t('guide.next', '下一步'),
        skip: t('guide.skip', '跳过'),
      }}
      floaterProps={{
        styles: {
          arrow: {
            length: 8,
            spread: 16,
          },
        },
      }}
    />
  );
};

export default TrainingGuider;

